import { CoreError } from './errors'

/**
 * ONE PAGE OF A KEYSET-PAGINATED LIST, as every registry read returns it.
 *
 * The cursor is OPAQUE: the UI passes it back verbatim and never parses it,
 * never treats it as an offset. `null` is the server saying the list ends
 * here. What a page carries beyond the items and the cursor — a total, a
 * limit, a has-more flag — is each contract's own, declared where that
 * contract's rows are.
 */
export interface CursorPage<TItem> {
  items: readonly TItem[]
  nextCursor: string | null
}

/** The page size every exhausting read asks for. One number, one answer. */
export const FULL_LIST_PAGE_SIZE = 100

/**
 * How many pages an exhausting read will take before it calls the answer
 * broken. At `FULL_LIST_PAGE_SIZE` that is 20 000 rows, which is past every
 * list this product exhausts and far short of what a browser tab survives.
 */
const PAGE_CEILING = 200

/**
 * WALK A KEYSET LIST TO ITS END, with the stopping conditions written ONCE.
 *
 * Six clients spelled this loop for themselves and none of them could stop. A
 * server that keeps answering with a cursor — a paging bug, a filter that does
 * not narrow, a clock-skewed keyset — turned a directory read into an infinite
 * loop inside an async function, and the tab accumulated every page until it
 * stopped responding while the surface showed a read still pending. These are
 * the reads behind the patient directory, the practitioner list, the task list
 * and the imaging queue, so that lands on the screens a clinic uses all day.
 *
 * Two things end it besides the server saying `null`: a page ceiling, and a
 * cursor the server has already handed out. The repeat check is the cheaper of
 * the two — a server cycling two cursors fails on the second page instead of
 * after two hundred — and both raise `malformed`, because an endless list is
 * an answer that does not satisfy the contract rather than a network fault the
 * reader could retry away.
 *
 * `what` names the list in those errors, so a stuck read says WHICH read.
 */
export async function exhaustCursor<TItem>(
  what: string,
  readPage: (cursor: string | undefined) => Promise<CursorPage<TItem>>,
): Promise<TItem[]> {
  const items: TItem[] = []
  const asked = new Set<string>()
  let cursor: string | undefined
  for (let page = 0; page < PAGE_CEILING; page += 1) {
    const answer = await readPage(cursor)
    items.push(...answer.items)
    cursor = answer.nextCursor ?? undefined
    if (cursor === undefined || cursor === '') {
      return items
    }
    if (asked.has(cursor)) {
      throw new CoreError('malformed', `${what} handed back a cursor it had already given`)
    }
    asked.add(cursor)
  }
  throw new CoreError('malformed', `${what} did not end within ${PAGE_CEILING} pages`)
}
